import { useState, useEffect } from 'react';
import {
  Button,
  Tag,
  Input,
  Form,
  Select,
  Empty,
  Collapse,
  Card,
  Space,
  Tooltip,
  Modal,
  Descriptions,
  Alert,
  Steps,
} from 'antd';
import {
  EyeOutlined,
  PlusOutlined,
  DeleteOutlined,
  LinkOutlined,
  RobotOutlined,
  CheckCircleOutlined,
  CodeOutlined,
  BulbOutlined,
  FileTextOutlined,
  CheckSquareOutlined,
} from '@ant-design/icons';
import type {
  BiddingCase,
  AnnotationData,
  ReplyDetail,
} from '../../types/index';

const { TextArea } = Input;
const { Option } = Select;
const { Panel } = Collapse;
const { Step } = Steps;

interface ReplyPanelProps {
  caseData: BiddingCase | null;
  annotationData: AnnotationData | null;
  selectedReplyId: string | null;
  onSelectReply: (replyId: string) => void;
  onPreviewDoc: () => void;
  onUpdateAnnotation: (data: AnnotationData) => void;
}

// 思维链预标注数据结构
interface ChainOfThought {
  factCheck: string; // 事实核查
  lawApplication: string; // 法规适用
  strategyChoice: string; // 策略选择
  finalConclusion: string; // 最终结论
}

// AI预标注的回复理由（思维链）
const generateAIChainOfThought = (reply: ReplyDetail): ChainOfThought => {
  // 根据已有reply_reasoning智能拆分，或使用默认值
  const reasoning = reply.reply_reasoning || '';

  // 简单拆分逻辑：按句号分割，尝试识别各部分
  const sentences = reasoning.split('。').filter((s) => s.trim());

  return {
    factCheck:
      sentences.find((s) => s.includes('依据') || s.includes('根据')) ||
      '待补充：事实核查部分',
    lawApplication:
      sentences.find((s) => s.includes('法') || s.includes('规定')) ||
      '待补充：法规适用部分',
    strategyChoice:
      sentences.find((s) => s.includes('因此') || s.includes('故')) ||
      '待补充：策略选择部分',
    finalConclusion: reply.reply_conclusion || '待补充：最终结论',
  };
};

const ReplyPanelV2 = ({
  caseData,
  annotationData,
  selectedReplyId,
  onSelectReply,
  onPreviewDoc,
  onUpdateAnnotation,
}: ReplyPanelProps) => {
  const [expandedReplies, setExpandedReplies] = useState<string[]>([]);
  const [jsonModalVisible, setJsonModalVisible] = useState(false);
  const [editingValues, setEditingValues] = useState<Record<string, any>>({});
  const [aiPreviews, setAiPreviews] = useState<Record<string, ChainOfThought>>({});
  const [showAIChain, setShowAIChain] = useState<Record<string, boolean>>({});

  useEffect(() => {
    // 初始化AI预标注
    if (annotationData) {
      const previews: Record<string, ChainOfThought> = {};
      annotationData.replyDetails.forEach((reply) => {
        previews[reply.item_id] = generateAIChainOfThought(reply);
      });
      setAiPreviews(previews);
    }
  }, [annotationData]);

  const handleReplyExpand = (replyId: string) => {
    setExpandedReplies((prev) =>
      prev.includes(replyId)
        ? prev.filter((id) => id !== replyId)
        : [...prev, replyId]
    );
    onSelectReply(replyId);
  };

  const handleFieldChange = (replyId: string, field: string, value: any) => {
    setEditingValues((prev) => ({
      ...prev,
      [replyId]: { ...prev[replyId], [field]: value },
    }));
  };

  const handleSaveReply = (reply: ReplyDetail) => {
    if (!annotationData) return;

    const updates = editingValues[reply.item_id] || {};

    // 如果使用了思维链，合并成reply_reasoning
    if (updates.chainOfThought) {
      const chain = updates.chainOfThought as ChainOfThought;
      updates.reply_reasoning = `${chain.factCheck}。${chain.lawApplication}。${chain.strategyChoice}`;
    }

    const updatedReplies = annotationData.replyDetails.map((r) =>
      r.item_id === reply.item_id ? { ...r, ...updates } : r
    );

    onUpdateAnnotation({
      ...annotationData,
      replyDetails: updatedReplies,
    });

    setEditingValues((prev) => {
      const newValues = { ...prev };
      delete newValues[reply.item_id];
      return newValues;
    });
  };

  const handleApplyAIChain = (replyId: string) => {
    const chain = aiPreviews[replyId];
    if (!chain) return;

    setEditingValues((prev) => ({
      ...prev,
      [replyId]: {
        ...prev[replyId],
        chainOfThought: chain,
        reply_reasoning: `${chain.factCheck}。${chain.lawApplication}。${chain.strategyChoice}`,
        reply_conclusion: chain.finalConclusion,
      },
    }));

    setShowAIChain((prev) => ({ ...prev, [replyId]: false }));
  };

  const getPositionColor = (position: string) => {
    const colors: Record<string, string> = {
      不采纳: 'red',
      部分采纳: 'orange',
      完全采纳: 'green',
      澄清说明: 'blue',
    };
    return colors[position] || 'default';
  };

  const getRelatedQuestion = (relatedId: string) => {
    return annotationData?.questionDetails.find((q) => q.item_id === relatedId);
  };

  const getFieldValue = (reply: ReplyDetail, field: string) => {
    const edited = editingValues[reply.item_id]?.[field];
    if (edited !== undefined) return edited;
    return reply[field as keyof ReplyDetail];
  };

  const getChainValue = (reply: ReplyDetail): ChainOfThought => {
    return (
      editingValues[reply.item_id]?.chainOfThought ||
      aiPreviews[reply.item_id] || {
        factCheck: '',
        lawApplication: '',
        strategyChoice: '',
        finalConclusion: reply.reply_conclusion || '',
      }
    );
  };

  return (
    <div className="annotation-panel">
      {/* 顶部标题栏 */}
      <div className="annotation-panel-header">
        <div className="flex items-center gap-2">
          <span>📤 质疑答复函 (输出端)</span>
          {annotationData && (
            <Tooltip title="查看完整JSON数据">
              <Button
                type="link"
               
                icon={<CodeOutlined />}
                onClick={() => setJsonModalVisible(true)}
              >
                JSON
              </Button>
            </Tooltip>
          )}
        </div>
        {caseData?.replyDoc && (
          <Button
            type="primary"
           
            icon={<EyeOutlined />}
            onClick={onPreviewDoc}
          >
            预览文档
          </Button>
        )}
      </div>

      <div className="annotation-panel-content">
        {!annotationData ? (
          <Empty description="请选择案件" />
        ) : (
          <>
            {/* 文档元数据 */}
            <Card className="mb-3" title="📋 文档元数据">
              <Descriptions column={2}>
                <Descriptions.Item label="文档类型">
                  {annotationData.docMeta.doc_type}
                </Descriptions.Item>
                <Descriptions.Item label="案件ID">
                  <span className="font-mono text-xs">
                    {annotationData.docMeta.case_id}
                  </span>
                </Descriptions.Item>
                <Descriptions.Item label="项目名称" span={2}>
                  {annotationData.docMeta.project_name}
                </Descriptions.Item>
                <Descriptions.Item label="发文日期">
                  {annotationData.docMeta.issue_date}
                </Descriptions.Item>
                <Descriptions.Item label="答复方">
                  {annotationData.docMeta.responder?.name}
                </Descriptions.Item>
                <Descriptions.Item label="整体结论" span={2}>
                  <Select
                   
                    className="w-40"
                    defaultValue={annotationData.docMeta.overall_decision}
                  >
                    <Option value="维持原判">维持原判</Option>
                    <Option value="部分采纳">部分采纳</Option>
                    <Option value="完全采纳">完全采纳</Option>
                    <Option value="澄清说明">澄清说明</Option>
                  </Select>
                </Descriptions.Item>
              </Descriptions>
            </Card>

            {/* 答复事项列表 - 折叠式 */}
            <div className="form-section">
              <div className="form-section-title">
                📌 答复事项列表
                <span className="text-xs text-gray-400 ml-2">
                  ({annotationData.replyDetails.length}个)
                </span>
              </div>

              <Collapse
                activeKey={expandedReplies}
                onChange={(keys) => setExpandedReplies(keys as string[])}
                ghost
                className="reply-collapse"
              >
                {annotationData.replyDetails.map((reply, index) => {
                  const relatedQuestion = getRelatedQuestion(
                    reply.related_inquiry_item_id
                  );

                  return (
                    <Panel
                      key={reply.item_id}
                      header={
                        <div
                          className="flex items-center justify-between w-full pr-4"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleReplyExpand(reply.item_id);
                          }}
                        >
                          <div className="flex items-center gap-2">
                            <span className="font-medium">
                              答复 #{index + 1}
                            </span>
                            <Tag color={getPositionColor(reply.reply_position)}>
                              {reply.reply_position}
                            </Tag>
                            {selectedReplyId === reply.item_id && (
                              <CheckCircleOutlined className="text-primary" />
                            )}
                          </div>
                          <span className="text-sm text-gray-500 truncate max-w-[150px]">
                            {reply.reply_conclusion?.slice(0, 30)}...
                          </span>
                        </div>
                      }
                      className={`mb-2 border rounded-lg overflow-hidden ${
                        selectedReplyId === reply.item_id
                          ? 'border-primary bg-blue-50'
                          : 'border-gray-200'
                      }`}
                    >
                      {/* 折叠内容：答复详情编辑 */}
                      <div className="p-3 bg-white">
                        {/* 关联信息 */}
                        <Alert
                          message={
                            <div className="flex items-center gap-2">
                              <LinkOutlined />
                              <span>
                                关联质疑: {relatedQuestion?.question_summary}
                              </span>
                              <Tag color="blue">
                                {relatedQuestion?.topic}
                              </Tag>
                            </div>
                          }
                          type="info"
                          showIcon={false}
                          className="mb-4"
                        />

                        <Form layout="vertical">
                          <Form.Item label="主题">
                            <Select
                              value={getFieldValue(reply, 'topic')}
                              onChange={(value) =>
                                handleFieldChange(reply.item_id, 'topic', value)
                              }
                            >
                              <Option value="资格条件">资格条件</Option>
                              <Option value="技术参数">技术参数</Option>
                              <Option value="报价评审">报价评审</Option>
                              <Option value="程序流程">程序流程</Option>
                              <Option value="中标结果">中标结果</Option>
                              <Option value="人员要求">人员要求</Option>
                              <Option value="其他">其他</Option>
                            </Select>
                          </Form.Item>

                          <Form.Item label="质疑点复述">
                            <Input
                              value={getFieldValue(reply, 'question_summary')}
                              onChange={(e) =>
                                handleFieldChange(
                                  reply.item_id,
                                  'question_summary',
                                  e.target.value
                                )
                              }
                            />
                          </Form.Item>

                          <Form.Item label="招标文件依据">
                            <div className="space-y-2">
                              {(reply.tender_clauses_cited || []).map(
                                (clause, idx) => (
                                  <div key={idx} className="flex gap-2">
                                    <Input
                                      defaultValue={clause}
                                     
                                      className="flex-1"
                                    />
                                    <Button
                                     
                                      danger
                                      icon={<DeleteOutlined />}
                                    />
                                  </div>
                                )
                              )}
                              <Button
                                type="dashed"
                               
                                block
                                icon={<PlusOutlined />}
                              >
                                添加依据
                              </Button>
                            </div>
                          </Form.Item>

                          <Form.Item label="援引法律法规">
                            <div className="space-y-2">
                              {(reply.laws_regulations_cited || []).map(
                                (law, idx) => (
                                  <Card
                                    key={idx}
                                   
                                    className="bg-gray-50"
                                  >
                                    <div className="font-medium text-sm">
                                      《{law.name}》
                                      {law.version_date && (
                                        <span className="text-gray-400 text-xs ml-1">
                                          ({law.version_date})
                                        </span>
                                      )}
                                    </div>
                                    {law.articles?.map((article, aidx) => (
                                      <div
                                        key={aidx}
                                        className="text-gray-600 text-xs mt-1 pl-2 border-l-2 border-gray-300"
                                      >
                                        第{article.article_index}条:{' '}
                                        {article.content?.slice(0, 100)}...
                                      </div>
                                    ))}
                                  </Card>
                                )
                              )}
                              <Button
                                type="dashed"
                               
                                block
                                icon={<PlusOutlined />}
                              >
                                添加法规
                              </Button>
                            </div>
                          </Form.Item>

                          {/* 思维链 (Chain of Thought) 回复理由 */}
                          <Card
                           
                            title={
                              <div className="flex items-center gap-2">
                                <BulbOutlined className="text-yellow-500" />
                                <span>💬 回复理由/论证 (思维链)</span>
                                <Tooltip title="AI辅助：按思维链方式组织论证逻辑">
                                  <RobotOutlined className="text-blue-500" />
                                </Tooltip>
                              </div>
                            }
                            className="mb-4 bg-gradient-to-r from-yellow-50 to-orange-50"
                            extra={
                              <Button
                                type="link"
                               
                                onClick={() =>
                                  setShowAIChain((prev) => ({
                                    ...prev,
                                    [reply.item_id]: !prev[reply.item_id],
                                  }))
                                }
                              >
                                {showAIChain[reply.item_id]
                                  ? '隐藏AI建议'
                                  : '显示AI建议'}
                              </Button>
                            }
                          >
                            {/* AI预标注建议 */}
                            {showAIChain[reply.item_id] && aiPreviews[reply.item_id] && (
                              <Alert
                                message="AI预标注建议 (思维链)"
                                description={
                                  <Steps
                                    direction="vertical"
                                   
                                    className="mt-2"
                                  >
                                    <Step
                                      title="事实核查"
                                      description={aiPreviews[reply.item_id].factCheck}
                                      icon={<FileTextOutlined />}
                                    />
                                    <Step
                                      title="法规适用"
                                      description={
                                        aiPreviews[reply.item_id].lawApplication
                                      }
                                      icon={<CheckSquareOutlined />}
                                    />
                                    <Step
                                      title="策略选择"
                                      description={
                                        aiPreviews[reply.item_id].strategyChoice
                                      }
                                      icon={<BulbOutlined />}
                                    />
                                    <Step
                                      title="最终结论"
                                      description={
                                        aiPreviews[reply.item_id].finalConclusion
                                      }
                                      icon={<CheckCircleOutlined />}
                                    />
                                  </Steps>
                                }
                                type="info"
                                showIcon
                                action={
                                  <Button
                                   
                                    type="primary"
                                    onClick={() => handleApplyAIChain(reply.item_id)}
                                  >
                                    采纳AI建议
                                  </Button>
                                }
                                className="mb-4"
                              />
                            )}

                            {/* 手动编辑思维链 */}
                            <div className="space-y-3">
                              <div>
                                <div className="text-xs text-gray-500 mb-1">
                                  <FileTextOutlined className="mr-1" />
                                  1. 事实核查
                                </div>
                                <TextArea
                                  rows={2}
                                  placeholder="核查质疑方提出的事实依据..."
                                  value={getChainValue(reply).factCheck}
                                  onChange={(e) =>
                                    handleFieldChange(reply.item_id, 'chainOfThought', {
                                      ...getChainValue(reply),
                                      factCheck: e.target.value,
                                    })
                                  }
                                />
                              </div>
                              <div>
                                <div className="text-xs text-gray-500 mb-1">
                                  <CheckSquareOutlined className="mr-1" />
                                  2. 法规适用
                                </div>
                                <TextArea
                                  rows={2}
                                  placeholder="引用适用的法律法规条款..."
                                  value={getChainValue(reply).lawApplication}
                                  onChange={(e) =>
                                    handleFieldChange(reply.item_id, 'chainOfThought', {
                                      ...getChainValue(reply),
                                      lawApplication: e.target.value,
                                    })
                                  }
                                />
                              </div>
                              <div>
                                <div className="text-xs text-gray-500 mb-1">
                                  <BulbOutlined className="mr-1" />
                                  3. 策略选择
                                </div>
                                <TextArea
                                  rows={2}
                                  placeholder="基于事实和法规选择处理策略..."
                                  value={getChainValue(reply).strategyChoice}
                                  onChange={(e) =>
                                    handleFieldChange(reply.item_id, 'chainOfThought', {
                                      ...getChainValue(reply),
                                      strategyChoice: e.target.value,
                                    })
                                  }
                                />
                              </div>
                            </div>
                          </Card>

                          <Form.Item label="✅ 最终结论 (Conclusion)">
                            <TextArea
                              rows={2}
                              value={getFieldValue(reply, 'reply_conclusion')}
                              onChange={(e) =>
                                handleFieldChange(
                                  reply.item_id,
                                  'reply_conclusion',
                                  e.target.value
                                )
                              }
                            />
                          </Form.Item>

                          <Form.Item label="结论分类标签">
                            <Select
                              value={getFieldValue(reply, 'reply_position')}
                              onChange={(value) =>
                                handleFieldChange(
                                  reply.item_id,
                                  'reply_position',
                                  value
                                )
                              }
                            >
                              <Option value="不采纳">不采纳</Option>
                              <Option value="部分采纳">部分采纳</Option>
                              <Option value="完全采纳">完全采纳</Option>
                              <Option value="澄清说明">澄清说明</Option>
                            </Select>
                          </Form.Item>

                          <Button
                            type="primary"
                            block
                            onClick={() => handleSaveReply(reply)}
                          >
                            保存修改
                          </Button>
                        </Form>
                      </div>
                    </Panel>
                  );
                })}
              </Collapse>

              <Button
                type="dashed"
                block
                icon={<PlusOutlined />}
                className="mt-3"
              >
                添加新答复
              </Button>
            </div>
          </>
        )}
      </div>

      {/* JSON数据弹窗 */}
      <Modal
        title="标注数据 (JSON)"
        open={jsonModalVisible}
        onCancel={() => setJsonModalVisible(false)}
        width={900}
        footer={[
          <Button key="close" onClick={() => setJsonModalVisible(false)}>
            关闭
          </Button>,
        ]}
      >
        {annotationData && (
          <div className="bg-gray-900 text-green-400 p-4 rounded overflow-auto max-h-[600px]">
            <pre className="text-xs whitespace-pre-wrap">
              {JSON.stringify(annotationData, null, 2)}
            </pre>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ReplyPanelV2;
